/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RecentActivities.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import RecentTickets from "./tickets/RecentTickets";
import {
  defaultConstats,
  TICKETS_TYPES,
} from "../../../../config/appConstants";
import ArchitectureConfig from "./architectureConfig/ArchitectureConfig";
import DashboardComponentsIndex from "./projectConfig/DashboardComponentsIndex";
import { useDispatch, useSelector } from "react-redux";
import { dashboardAction, HandleApiActions } from "../../../../redux/actions";
import { componentStyle } from "../../../../config/appTheme";
import CustomFilterV2 from "../../common/filter/CustomFilterV2";
import CustomSelectV2 from "../../common/select/CustomSelectV2";

function RecentActivities({
  recentEEComponent,
  recentSoftwareModule,
  recentVehicle,
}) {
  const dispatch = useDispatch();
  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails
  );
  const selectedVariant = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails
  );
  const [radioTabValue, setRadioTabValue] = useState("ticket");
  const [componentTabValue, setComponentTabValue] = useState("ee");
  const [tempRAFilterValue, setTempRAFilterValue] = useState("ticket");
  const [tempRAComponentValue, setTempRAComponentValue] = useState("ee");
  const [anchorElRAFilter, setAnchorElRAFilter] = useState(null);
  const [openRAFilter, setOpenRAFilter] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedVarientId, setSelectedVarientId] = useState(null);
  const [eeTicketDetails, setEETicketDetails] = useState([]); // EE tickets details
  const [swmTicketDetails, setSWMTicketDetails] = useState([]); // SWM tickets details
  const [vsTicketDetails, setVSTicketDetails] = useState([]); // Vehicle System tickets details
  const [vcTicketDetails, setVCTicketDetails] = useState([]); // Vehicle Component tickets details
  const [componentDetails, setComponentDetails] = useState([]);
  const [componentHeading, setComponentHeading] = useState("");
  const [filterOptions, setFilterOptions] = useState([]);
  const [hasTicketNeedMore, setHasTicketNeedMore] = useState(false);
  const [renderingComponent, setRenderingComponent] = useState(<></>);

  // get Tickets
  const getTicketDetails = async ({ params = {}, setter }) => {
    let apiDetails = dashboardAction.get_tickets;
    delete apiDetails.action_type;

    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          ...params,
          project_id: selectedProject?.project_id,
          variant_id: selectedVariant?.variant_id,
          page_size: defaultConstats.recentDataPageSize,
          page_number: defaultConstats.recentDataPageNumber,
          status: "100,101,150,200,300,400,500,600,999",
          sort_column: "created_at",
          sort_order: "DESC",
        },
        show_toast: false,
      })
    );
    if (resp.error) {
      setter([]);
      setHasTicketNeedMore(false);
    } else {
      setHasTicketNeedMore(resp?.data?.length > 5);
      const firstFive = resp.data.slice(0, 5);
      setter(firstFive);

      return resp.data;
    }
  };

  const handleRAFilterIconClick = (event) => {
    setAnchorElRAFilter(event.currentTarget);
    setOpenRAFilter(true);
  };

  const handleRAFilterIconClose = () => {
    setAnchorElRAFilter(null);
    setOpenRAFilter(false);
  };

  const handleRAFilterButton = (action) => {
    switch (action) {
      case "cancel":
        handleRAFilterIconClose();
        break; // prevents fall-through
      case "apply":
        setRadioTabValue(tempRAFilterValue);
        setComponentTabValue(tempRAComponentValue);
        handleRAFilterIconClose();
        break;
      default:
    }
  };

  useEffect(() => {
    setSelectedProjectId(selectedProject?.project_id);
    setSelectedVarientId(selectedVariant?.variant_id);
  }, [selectedProject?.project_id, selectedVariant?.variant_id]);

  // Use effect to get the ticket details
  useEffect(() => {

    if (selectedProjectId) {
      if (radioTabValue === "ticket") {
        if (componentTabValue === "ee") {
          getTicketDetails({
            params: { tickets_type: TICKETS_TYPES.EE },
            setter: setEETicketDetails,
          });
        } else if (componentTabValue === "swm") {
          getTicketDetails({
            params: { tickets_type: TICKETS_TYPES.SWM },
            setter: setSWMTicketDetails,
          });
        } else if (componentTabValue === "vs") {
          getTicketDetails({
            params: { tickets_type: TICKETS_TYPES.VEHICLE_SYSTEM },
            setter: setVSTicketDetails,
          });
        } else if (componentTabValue === "vc") {
          getTicketDetails({
            params: { tickets_type: TICKETS_TYPES.VEHICLE_COMPONENT },
            setter: setVCTicketDetails,
          });
        }
      }
    }
  }, [selectedProjectId, selectedVarientId, radioTabValue, componentTabValue]);

  const getFilterOptionBasedOnRadioValue = () => {
    switch (tempRAFilterValue) {
      case "ticket":
        return [
          { value: "vs", label: "Vehicle Systems" },
          { value: "vc", label: "Vehicle Components" },
          { value: "ee", label: "EE Components" },
          { value: "swm", label: "Software Modules" },
        ];
      case "pcd":
        return [
          { value: "ee", label: "EE Components" },
          { value: "swm", label: "Software Modules" },
        ];
      default:
        return [];
    }
  };

  const getComponentsBasedOnFilter = () => {
    let filterResult = `${radioTabValue}_${componentTabValue}`;
    switch (filterResult) {
      case "ticket_ee":
        setComponentHeading("EE Component Tickets");
        return (
          <Box>
            <RecentTickets
              tickets_type={TICKETS_TYPES.EE}
              ticketDetails={eeTicketDetails}
              hasTicketNeedMore={hasTicketNeedMore}
            />
          </Box>
        );
      case "ticket_swm":
        setComponentHeading("Software Module Tickets");
        return (
          <RecentTickets
            tickets_type={TICKETS_TYPES.SWM}
            ticketDetails={swmTicketDetails}
            hasTicketNeedMore={hasTicketNeedMore}
          />
        );
      case "ticket_vs":
        setComponentHeading("Vehicle System Tickets");
        return (
          <RecentTickets
            tickets_type={TICKETS_TYPES.VEHICLE_SYSTEM}
            ticketDetails={vsTicketDetails}
            hasTicketNeedMore={hasTicketNeedMore}
          />
        );
      case "ticket_vc":
        setComponentHeading("Vehicle Component Tickets");
        return (
          <RecentTickets
            tickets_type={TICKETS_TYPES.VEHICLE_COMPONENT}
            ticketDetails={vcTicketDetails}
            hasTicketNeedMore={hasTicketNeedMore}
          />
        );
      case "pcd_ee":
        setComponentHeading("EE Components");
        return <DashboardComponentsIndex type="EE" />;
      case "pcd_swm":
        setComponentHeading("Software Module Components");
        return <DashboardComponentsIndex type="SWM" />;
      case "pcd_vs":
        setComponentHeading("Vehicle System");
        return <DashboardComponentsIndex type="VS" />;
      case "pcd_vc":
        setComponentHeading("Vehicle Component");
        return <DashboardComponentsIndex type="VC" />;
      case "acs_":
        setComponentHeading("");
        return <ArchitectureConfig />;
      default:
        return <></>;
    }
  };

  useEffect(() => {
    setRenderingComponent(getComponentsBasedOnFilter());
  }, [
    radioTabValue,
    componentTabValue,
    eeTicketDetails,
    swmTicketDetails,
    vsTicketDetails,
    vcTicketDetails,
  ]);

  useEffect(() => {
    const options = getFilterOptionBasedOnRadioValue();
    setFilterOptions(options);

    // Retain component value
    if (options.length > 0) {
      if (
        tempRAComponentValue &&
        tempRAComponentValue != "vs" &&
        tempRAComponentValue != "vc"
      ) {
        setTempRAComponentValue(tempRAComponentValue);
      } else {
        setTempRAComponentValue("");
      }
    } else {
      setTempRAComponentValue("");
    }
  }, [tempRAFilterValue]);

  useEffect(() => {
    const initialOptions = getFilterOptionBasedOnRadioValue();
    setFilterOptions(initialOptions);
    if (initialOptions.length > 0) {
      setTempRAComponentValue(initialOptions[0].value);
    }
    setRenderingComponent(getComponentsBasedOnFilter());
  }, []);

  return (
    <Box>
      <Box>
        <Typography sx={{ ...componentStyle.font.tabLevelHeader }}>
          Recent Activities
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
        }}
      >
        <CustomFilterV2
          anchorEl={anchorElRAFilter}
          open={openRAFilter}
          widthPerColumn={"350px"}
          handleClick={handleRAFilterIconClick}
          handleFilterButton={handleRAFilterButton}
          handleClose={handleRAFilterIconClose}
          actionButtonStyle={{
            width: "100%",
            justifyContent: "space-between",
          }}
          sections={[
            {
              heading: "Recent Activities",
              content: (
                <Box
                  sx={{
                    width: "100%",
                    p: 2,
                    justifyContent: "center",
                  }}
                >
                  <Box>
                    <CustomSelectV2
                      optionsList={[
                        { value: "ticket", label: "Ticket" },
                        {
                          value: "pcd",
                          label: "Project Configurations Details",
                        },
                        {
                          value: "acs",
                          label: "Architecture Configuration Status",
                        },
                      ]}
                      selectedValue={tempRAFilterValue}
                      handleOnChange={(e) =>
                        setTempRAFilterValue(e.target.value)
                      }
                      label={"Select Recent Activity"}
                    />
                  </Box>

                  {filterOptions.length > 0 && (
                    <Box>
                      <CustomSelectV2
                        optionsList={filterOptions}
                        selectedValue={tempRAComponentValue}
                        handleOnChange={(e) =>
                          setTempRAComponentValue(e.target.value)
                        }
                        label={"Select Component or Module"}
                      />
                    </Box>
                  )}
                </Box>
              ),
            },
          ]}
        />
      </Box>
      <Box
        sx={{
          backgroundColor: "#ffffff",
          mt: 2,
          borderRadius: "1rem",
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "left" }}>
            {componentHeading !== "" && (
              <Typography sx={{ ...componentStyle.font.tabLevelHeader }}>
                {componentHeading}
              </Typography>
            )}
          </Box>
          {renderingComponent}
        </Box>
      </Box>
    </Box>
  );
}

export default RecentActivities;
